/**
 * PixelField — a bed of tiny squares that grow in from the centre and keep
 * shimmering, ported from React Bits' `PixelCard`
 * (reactbits.dev/components/pixel-card) as a *layer*: drop it inside any
 * `<Card>` (which is already `relative`) rather than wrapping content in a
 * new card. The canvas sits behind the content and takes no pointer events.
 *
 * Colours come from the theme: three CSS custom properties on the field
 * (`--pixel-1` … `--pixel-3`, defaulting to mixes of `--primary` into
 * `--card`) are resolved through `getComputedStyle`, so light and dark
 * themes and the regime accents all work without hard-coded hex. Reduced
 * motion draws the field at rest on the first frame: no grow-in, no shimmer.
 *
 * @module components/ui/pixel-field
 */
import { useEffect, useRef, type CSSProperties } from "react";

import { cn } from "~/lib/utils";

/** Grid origins for a `width`×`height` field with a pixel every `gap`. */
export function pixelGrid(
  width: number,
  height: number,
  gap: number,
): ReadonlyArray<{ readonly x: number; readonly y: number; readonly distance: number }> {
  const step = Math.max(1, Math.floor(gap));
  const cells: Array<{ x: number; y: number; distance: number }> = [];
  for (let x = 0; x < width; x += step) {
    for (let y = 0; y < height; y += step) {
      const dx = x - width / 2;
      const dy = y - height / 2;
      cells.push({ x, y, distance: Math.sqrt(dx * dx + dy * dy) });
    }
  }
  return cells;
}

/** React Bits' speed curve: 0–100 → 0–0.1 px per frame, nothing when motion is reduced. */
export function effectiveSpeed(value: number, reducedMotion: boolean): number {
  if (value <= 0 || reducedMotion) return 0;
  return Math.min(value, 100) * 0.001;
}

class Pixel {
  size = 0;
  counter = 0;
  isIdle = false;
  /** Fully grown in; shimmering from here on. */
  grown = false;
  private isReverse = false;
  private readonly speed: number;
  private readonly sizeStep = Math.random() * 0.4;
  private readonly minSize = 0.5;
  private readonly maxSizeInteger = 2;
  private readonly maxSize: number;
  private readonly counterStep: number;

  constructor(
    private readonly ctx: CanvasRenderingContext2D,
    private readonly x: number,
    private readonly y: number,
    private readonly color: string,
    speed: number,
    private readonly delay: number,
    fieldSize: number,
    /** Reduced motion: appear at full size on the first frame, no grow-in. */
    private readonly snap: boolean,
  ) {
    this.speed = (Math.random() * 0.8 + 0.1) * speed;
    this.maxSize = Math.random() * (this.maxSizeInteger - this.minSize) + this.minSize;
    this.counterStep = Math.random() * 4 + fieldSize * 0.01;
  }

  private draw() {
    const centerOffset = this.maxSizeInteger * 0.5 - this.size * 0.5;
    this.ctx.fillStyle = this.color;
    this.ctx.fillRect(this.x + centerOffset, this.y + centerOffset, this.size, this.size);
  }

  appear() {
    this.isIdle = false;
    if (this.counter <= this.delay) {
      this.counter += this.counterStep;
      return;
    }
    if (this.snap) {
      this.size = this.maxSize;
      this.grown = true;
      this.draw();
      return;
    }
    if (this.size >= this.maxSize) this.grown = true;
    if (this.grown) this.shimmer();
    else this.size += this.sizeStep;
    this.draw();
  }

  disappear() {
    this.grown = false;
    this.counter = 0;
    if (this.size <= 0) {
      this.isIdle = true;
      return;
    }
    this.size -= 0.1;
    this.draw();
  }

  private shimmer() {
    if (this.size >= this.maxSize) this.isReverse = true;
    else if (this.size <= this.minSize) this.isReverse = false;
    this.size += this.isReverse ? -this.speed : this.speed;
  }
}

export interface PixelFieldProps {
  /** Distance between pixels, px. */
  readonly gap?: number;
  /** 0–100; how lively the shimmer is. */
  readonly speed?: number;
  /** Fade the field's edges into the card so text above stays legible. */
  readonly fade?: boolean;
  /** Where the field is fullest, as a CSS mask: defaults to the card's middle. */
  readonly mask?: string;
  readonly className?: string;
  readonly style?: CSSProperties;
}

const COLOR_VARS = ["--pixel-1", "--pixel-2", "--pixel-3"] as const;

/**
 * Resolve each colour variable to a canvas-safe colour through the DOM: a
 * probe span in the host carries the canvas's inline values, and its
 * computed `color` is what the theme's `color-mix` works out to.
 */
function resolveColors(canvas: HTMLCanvasElement, host: HTMLElement): string[] {
  const probe = document.createElement("span");
  probe.style.position = "absolute";
  probe.style.visibility = "hidden";
  for (const name of COLOR_VARS) probe.style.setProperty(name, canvas.style.getPropertyValue(name));
  host.appendChild(probe);
  const colors = COLOR_VARS.map((name) => {
    probe.style.color = `var(${name})`;
    return getComputedStyle(probe).color;
  }).filter((color) => color && color !== "rgba(0, 0, 0, 0)");
  probe.remove();
  return colors.length > 0 ? colors : ["currentColor"];
}

export function PixelField({
  gap = 7,
  speed = 30,
  fade = true,
  mask = "radial-gradient(ellipse 95% 85% at 50% 55%, black 30%, transparent 88%)",
  className,
  style,
}: PixelFieldProps) {
  const canvasRef = useRef<HTMLCanvasElement>(null);

  useEffect(() => {
    const canvas = canvasRef.current;
    const host = canvas?.parentElement;
    if (!canvas || !host) return;
    const ctx = canvas.getContext("2d");
    if (!ctx) return;
    const reducedMotion = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    // No shimmer to keep: reduced motion, or a field asked to hold still.
    const still = reducedMotion || effectiveSpeed(speed, reducedMotion) === 0;
    let pixels: Pixel[] = [];
    let frame: number | null = null;
    let previous = performance.now();

    const init = () => {
      const rect = host.getBoundingClientRect();
      const width = Math.floor(rect.width);
      const height = Math.floor(rect.height);
      if (width === 0 || height === 0) return;
      canvas.width = width;
      canvas.height = height;
      const colors = resolveColors(canvas, host);
      const pixelSpeed = effectiveSpeed(speed, reducedMotion);
      pixels = pixelGrid(width, height, gap).map(
        (cell) =>
          new Pixel(
            ctx,
            cell.x,
            cell.y,
            colors[Math.floor(Math.random() * colors.length)]!,
            pixelSpeed,
            reducedMotion ? 0 : cell.distance,
            width + height,
            reducedMotion,
          ),
      );
    };

    const animate = (step: "appear" | "disappear") => {
      frame = requestAnimationFrame(() => animate(step));
      const now = performance.now();
      const passed = now - previous;
      const interval = 1000 / 60;
      if (passed < interval) return;
      previous = now - (passed % interval);
      ctx.clearRect(0, 0, canvas.width, canvas.height);
      let allIdle = true;
      let allGrown = true;
      for (const pixel of pixels) {
        pixel[step]();
        if (!pixel.isIdle) allIdle = false;
        if (!pixel.grown) allGrown = false;
      }
      // Nothing left to move: a disappeared field once it is gone, a still
      // field once it has grown in.
      if ((step === "disappear" && allIdle) || (step === "appear" && still && allGrown)) {
        if (frame !== null) cancelAnimationFrame(frame);
        frame = null;
      }
    };
    const run = (step: "appear" | "disappear") => {
      if (frame !== null) cancelAnimationFrame(frame);
      frame = requestAnimationFrame(() => animate(step));
    };

    init();
    run("appear");
    const observer = new ResizeObserver(() => {
      init();
      run("appear");
    });
    observer.observe(host);
    // Off-screen, the frame loop stops; back on, it resumes where it was.
    const visibility = new IntersectionObserver(([entry]) => {
      if (!entry) return;
      if (entry.isIntersecting) run("appear");
      else if (frame !== null) {
        cancelAnimationFrame(frame);
        frame = null;
      }
    });
    visibility.observe(host);
    return () => {
      observer.disconnect();
      visibility.disconnect();
      if (frame !== null) cancelAnimationFrame(frame);
    };
  }, [gap, speed]);

  return (
    <canvas
      ref={canvasRef}
      aria-hidden="true"
      data-slot="pixel-field"
      className={cn("pointer-events-none absolute inset-0 -z-10 block size-full rounded-[inherit]", className)}
      style={{
        ["--pixel-1" as string]: "color-mix(in oklab, var(--primary) 30%, var(--card))",
        ["--pixel-2" as string]: "color-mix(in oklab, var(--primary) 55%, var(--card))",
        ["--pixel-3" as string]: "color-mix(in oklab, var(--primary) 85%, var(--card))",
        ...(fade
          ? {
              maskImage: mask,
              WebkitMaskImage: mask,
            }
          : {}),
        ...style,
      }}
    />
  );
}
